import { useMemo } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useUserPermissions } from './app/apis/user-details';
import LoadingIndicator from './app/shared/LoadingIndicator';
import {
  CampaignsPermissionsKey,
  checkPermissions,
  LoyaltyPermissionsKey,
  OrdersPermissionsKey,
  ProductsPermissionsKey,
  ShopsPermissionsKey,
} from './app/shared/utils/permissions';
import { useTranslation } from 'react-i18next';

// TODO: Remove restaurantUserPermissions once new RBAC has been fully taken in use
type GuardedRouteProps = {
  requiredPermissions: Array<string>;
  restaurantUserPermissions?: Array<string>;
};

export const GuardedRoute = ({ requiredPermissions, restaurantUserPermissions }: GuardedRouteProps) => {
  const { t } = useTranslation();

  const {
    isErrorCampaigns,
    isErrorLoyalty,
    isLoadingCampaigns,
    isLoadingLoyalty,
    isErrorProducts,
    isLoadingProducts,
    isLoadingShops,
    isErrorShops,
    isLoadingOrders,
    isErrorOrders,
    roles,
    isLoading,
  } = useUserPermissions();

  const location = useLocation();
  const ErrorComponent = useMemo(() => () => <p>{t('global.error')}</p>, [t]);

  if (isErrorCampaigns && requiredPermissions.some((x) => x.startsWith(CampaignsPermissionsKey))) {
    return <ErrorComponent />;
  }

  if (isErrorLoyalty && requiredPermissions.some((x) => x.startsWith(LoyaltyPermissionsKey))) {
    return <ErrorComponent />;
  }

  if (isErrorProducts && requiredPermissions.some((x) => x.startsWith(ProductsPermissionsKey))) {
    return <ErrorComponent />;
  }

  if (isErrorShops && requiredPermissions.some((x) => x.startsWith(ShopsPermissionsKey))) {
    return <ErrorComponent />;
  }

  if (isErrorOrders && requiredPermissions.some((x) => x.startsWith(OrdersPermissionsKey))) {
    return <ErrorComponent />;
  }

  // The flags above cover those role queries, but not the users API's own — so a route that admits
  // restaurant users by an RBAC role has to wait for the whole set, or it would redirect them away in
  // the moment before their roles land.
  if (
    isLoadingCampaigns ||
    isLoadingLoyalty ||
    isLoadingProducts ||
    isLoadingShops ||
    isLoadingOrders ||
    (restaurantUserPermissions && isLoading)
  ) {
    return <LoadingIndicator message={t('global.loading')} />;
  }

  const hasRequiredPermissions =
    checkPermissions(roles || [], requiredPermissions) ||
    (restaurantUserPermissions && checkPermissions(roles || [], restaurantUserPermissions));

  if (!hasRequiredPermissions) {
    return <Navigate to="/home" state={{ from: location }} replace />;
  }

  return <Outlet />;
};
